import {
  BooleanField,
  DataTable,
  Labeled,
  ReferenceManyField,
  ReferenceField,
  RichTextField,
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  UpdateButton,
  useRecordContext,
  useCanAccess,
  Pagination,
} from 'react-admin';
import { CheckCircle, Replay } from '@mui/icons-material';
import { ReactElement } from 'react';
import TzDateField from '../../Components/TzDate/TzDateField';
import { TrackedErrorIssueResource } from '../../Entities/TrackedErrorIssue';
import { Paper } from '@mui/material';

function Actions(): ReactElement {
  const record = useRecordContext<TrackedErrorIssueResource>();

  return (
    <TopToolbar sx={{ gap: 1 }}>
      <UpdateButton
        label="Mark resolved"
        color="success"
        sx={{ color: 'text.primary' }}
        icon={<CheckCircle />}
        variant="contained"
        disabled={record?.attributes.is_resolved ?? true}
        data={{ attributes: { is_resolved: true } }}
      />
      <UpdateButton
        label="Mark unresolved"
        color="warning"
        icon={<Replay />}
        variant="contained"
        sx={{ color: 'text.primary' }}
        disabled={!(record?.attributes.is_resolved ?? false)}
        data={{ attributes: { is_resolved: false } }}
      />
    </TopToolbar>
  );
}

export default function TrackedErrorIssueShow(): ReactElement {
  const trackedErrorWriteAccess = useCanAccess({ resource: 'tracked_error_issue', action: 'edit' });

  return (
    <Show<TrackedErrorIssueResource>
      actions={trackedErrorWriteAccess.canAccess && <Actions />}
      queryOptions={{ meta: { prefetch: ['merchant'] } }}
    >
      <SimpleShowLayout>
        <TextField source="attributes.category" label="Category" />
        <Labeled label="Entity">
          <ReferenceField reference="merchants" source="relationships.merchant.data.id" />
        </Labeled>
        <TextField source="attributes.operation" label="Operation" />
        <TextField source="attributes.title" label="Title" />
        <Labeled label="Last Message">
          <Paper elevation={2} sx={{ padding: 2 }}>
            <RichTextField source="attributes.last_message" />
          </Paper>
        </Labeled>
        <BooleanField source="attributes.is_resolved" label="Resolved" />
        <TzDateField
          source="attributes.first_occurred_at_utc"
          label="First Occurred"
          showTime
          serverTimezone="UTC"
        />
        <TzDateField
          source="attributes.last_occurred_at_utc"
          label="Last Occurred"
          showTime
          serverTimezone="UTC"
        />
        <TzDateField
          source="attributes.resolved_at_utc"
          label="Resolved At"
          showTime
          serverTimezone="UTC"
        />
        <TextField source="attributes.resolved_by" label="Resolved By" />
        <Labeled label="Occurrences" sx={{ width: '100%' }}>
          <ReferenceManyField
            pagination={<Pagination />}
            reference="tracked_error_occurrences"
            target="tracked_error_issue_id"
          >
            <DataTable bulkActionButtons={false}>
              <DataTable.Col label="Occurred At">
                <TzDateField source="attributes.occurred_at_utc" showTime serverTimezone="UTC" />
              </DataTable.Col>
              <DataTable.Col label="Message" source="attributes.message" field={RichTextField} />
              <DataTable.Col label="Detail" source="attributes.detail" field={RichTextField} />
            </DataTable>
          </ReferenceManyField>
        </Labeled>
      </SimpleShowLayout>
    </Show>
  );
}
